import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { iziplayAuth, iziplaySearch, iziplaySocial } from '../tools/api'
import { isErrorResponse } from '../tools/typeGuards'
import { setLang } from '../state/ui'
import { queryKeys } from './keys'

export interface User {
  username: string
  mailHash: string
  role: -1 | 0 | 1
  trakt?: string
}

export interface Settings {
  providers: Record<string, boolean>
  subtitles: string[]
  lang?: Iziplay.Lang
  [key: string]: any
}

export const DEFAULT_USER: User = { username: 'John Doe', mailHash: '', role: -1 }

/*
 * An invalid token is terminal: drop it and bounce to the login page rather
 * than letting React Query retry a request that can only fail again.
 */
function handleAuthFailure(error: unknown): never {
  if (isErrorResponse(error) && error.response?.data.err === 'Invalid token') {
    window.localStorage.removeItem('token')
    window.location.href = '/'
  }
  throw error
}

export function useUser() {
  return useQuery({
    queryKey: queryKeys.user,
    queryFn: async (): Promise<User> => {
      try {
        const res = await iziplayAuth().get<Partial<User>>('/me/')
        return { ...DEFAULT_USER, ...res.data }
      } catch (error) {
        if (isAxiosError(error)) handleAuthFailure(error)
        throw error
      }
    },
    // One retry, so a single blip on auth does not cost the session. An
    // invalid token redirects from handleAuthFailure rather than retrying.
    retry: 1,
    staleTime: 5 * 60 * 1000
  })
}

/*
 * Settings carry the user's language, and the provider list is only meaningful
 * once cross-referenced with the providers the search service actually offers,
 * so the two are resolved together. A search outage leaves no provider on
 * offer rather than failing the whole query: the language and subtitle
 * preferences come from social alone.
 */
export const settingsQuery = queryOptions({
  queryKey: queryKeys.settings,
  queryFn: async (): Promise<Settings> => {
    const res = await iziplaySocial().get<Settings>('/settings')
    const settings = res.data

    if (settings.lang) setLang(settings.lang)

    const providers = await iziplaySearch()
      .get<string[]>('/providers')
      .then(res => res.data)
      .catch((error: unknown) => {
        console.error('failed to load the search providers', error)
        return []
      })
    settings.providers = Object.fromEntries(
      providers.map(name => [name, settings.providers?.[name] !== false])
    )

    return settings
  },
  staleTime: 5 * 60 * 1000
})

export function useSettings() {
  return useQuery(settingsQuery)
}

export function useSaveSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => iziplaySocial().put('/settings', patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings })
  })
}
